import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, ClockIcon } from '@heroicons/react/outline';
import Button from '../../../components/Button';
import Steps from '../../../common/components/Steps';
import { ReactComponent as TemplateIcon } from '../../ProjectWizardTemplates/components/brain.svg';
import ProjectWizardEditControls from './ProjectWizardEditControls';
import formatRelativeTimeAgo from './formatRelativeTimeAgo';

const ProjectWizardHeader = ({
  projectName,
  hasDraft,
  draftSavedAt,
  lastPublishedAt,
  isEditMode,
  canEditProject,
  isProjectReady,
  isEditLocked,
  isEditLockedByOther,
  lockHolderDisplayName,
  lockCheckedOutAt,
  onBack,
  steps,
  onEnterEditMode,
  onExitEditMode,
  onSaveDraft,
  onPreparePublish,
  onClearDraft,
  onCommitDraft,
  onCheckPendingChanges,
  onDiscardChanges,
  hasPendingChangesRef,
  onRegisterLeaveConfirmation,
  projectId,
  requestEditDialog,
  takeoverRequestDialog,
}) => {
  const [, setNow] = useState(Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(Date.now());
    }, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const draftSavedLabel = formatRelativeTimeAgo(draftSavedAt);
  const lastPublishedLabel = formatRelativeTimeAgo(lastPublishedAt);
  const lockCheckedOutLabel = formatRelativeTimeAgo(lockCheckedOutAt);

  return (
    <div className="px-6 pt-6 stack-2">
      <div className="p-6 rounded-md bg-pw2-header dark:bg-disabled">
        <div className="cluster-2 items-start">
          <Button
            data-testid="page-button-back"
            type="tertiary"
            icon={<ArrowLeftIcon />}
            onClick={onBack}
          />
          <TemplateIcon className="w-8 h-8 flex-shrink-0" />
          <div className="stack-2 flex-1 min-w-0">
            <div className="cluster-4 items-center justify-between gap-4">
              <div className="cluster-3 items-center min-w-0 flex-wrap">
                <h1
                  data-testid="page-header"
                  className="text-lg font-medium mt-0 truncate"
                >
                  {projectName}
                </h1>
                {hasDraft && (
                  <span
                    data-testid="label-changes-not-publish"
                    className="inline-flex items-center rounded-full border border-warning bg-warning-muted dark:bg-neutral-800 px-3 py-0.5 text-xs font-medium text-on-warning dark:text-warning whitespace-nowrap"
                  >
                    Changes not published
                  </span>
                )}
              </div>
              <div className="flex-shrink-0">
                <ProjectWizardEditControls
                  isEditMode={isEditMode}
                  canEditProject={canEditProject}
                  isProjectReady={isProjectReady}
                  isEditLocked={isEditLocked}
                  hasDraft={hasDraft}
                  onEnterEditMode={onEnterEditMode}
                  onExitEditMode={onExitEditMode}
                  onSaveDraft={onSaveDraft}
                  onPreparePublish={onPreparePublish}
                  onClearDraft={onClearDraft}
                  onCommitDraft={onCommitDraft}
                  onCheckPendingChanges={onCheckPendingChanges}
                  onDiscardChanges={onDiscardChanges}
                  hasPendingChangesRef={hasPendingChangesRef}
                  onRegisterLeaveConfirmation={onRegisterLeaveConfirmation}
                  projectId={projectId}
                  requestEditDialog={requestEditDialog}
                  takeoverRequestDialog={takeoverRequestDialog}
                />
              </div>
            </div>
            {(hasDraft && draftSavedLabel) || lastPublishedLabel ? (
              <div className="cluster-4 items-center text-sm text-neutral-300 dark:text-neutral-300 flex-wrap">
                {lastPublishedLabel && (
                  <span data-testid="label-last-published-at">
                    Last published {lastPublishedLabel}
                  </span>
                )}
                {hasDraft && draftSavedLabel && lastPublishedLabel && (
                  <div className="w-px bg-divider h-4" aria-hidden="true" />
                )}
                {hasDraft && draftSavedLabel && (
                  <span
                    data-testid="label-draft-saved-at"
                    className="cluster-1 items-center"
                  >
                    <ClockIcon className="h-4 w-4" />
                    <span>Draft saved {draftSavedLabel}</span>
                  </span>
                )}
                {isEditLockedByOther && (
                  <span
                    data-testid="banner-project-edit-lock"
                    className="inline-flex items-center rounded-full border border-warning bg-warning-muted dark:bg-neutral-800 px-3 py-0.5 text-xs font-medium text-on-warning dark:text-warning"
                  >
                    {lockHolderDisplayName || 'Another user'} is editing this
                    project. Only one person can edit at a time
                    {lockCheckedOutLabel
                      ? ` (editing since ${lockCheckedOutLabel})`
                      : ''}
                    . You can browse all steps in view-only mode until editing
                    is available.
                  </span>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="p-6 rounded-md bg-pw2-header dark:bg-disabled">
        <Steps steps={steps} />
      </div>
    </div>
  );
};

ProjectWizardHeader.propTypes = {
  projectName: PropTypes.string,
  hasDraft: PropTypes.bool,
  draftSavedAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  lastPublishedAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  isEditMode: PropTypes.bool,
  canEditProject: PropTypes.bool,
  isProjectReady: PropTypes.bool,
  isEditLocked: PropTypes.bool,
  isEditLockedByOther: PropTypes.bool,
  lockHolderDisplayName: PropTypes.string,
  lockCheckedOutAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  onBack: PropTypes.func.isRequired,
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEnterEditMode: PropTypes.func.isRequired,
  onExitEditMode: PropTypes.func.isRequired,
  onSaveDraft: PropTypes.func.isRequired,
  onPreparePublish: PropTypes.func.isRequired,
  onClearDraft: PropTypes.func.isRequired,
  onCommitDraft: PropTypes.func.isRequired,
  onCheckPendingChanges: PropTypes.func.isRequired,
  onDiscardChanges: PropTypes.func.isRequired,
  hasPendingChangesRef: PropTypes.shape({ current: PropTypes.bool }),
  onRegisterLeaveConfirmation: PropTypes.func,
  projectId: PropTypes.string,
  requestEditDialog: PropTypes.object,
  takeoverRequestDialog: PropTypes.object,
};

ProjectWizardHeader.defaultProps = {
  projectName: '',
  hasDraft: false,
  draftSavedAt: null,
  lastPublishedAt: null,
  isEditMode: false,
  canEditProject: false,
  isProjectReady: false,
  isEditLocked: false,
  isEditLockedByOther: false,
  lockHolderDisplayName: '',
  lockCheckedOutAt: null,
  hasPendingChangesRef: null,
  onRegisterLeaveConfirmation: null,
  projectId: null,
  requestEditDialog: null,
  takeoverRequestDialog: null,
};

export default ProjectWizardHeader;
